import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserPlus, FileSignature, Receipt, CreditCard, FileText, BarChart3,
  UserCog, Settings, LogOut, Menu, X, HeartHandshake,
} from 'lucide-react';
import { care, getUser } from './careApi';
import { AskHost } from './AskDialog';

const NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/care/dashboard', roles: ['admin', 'staff', 'finance'] },
  { label: 'Residents', icon: Users, path: '/care/residents', roles: ['admin', 'staff', 'finance'] },
  { label: 'New Registration', icon: UserPlus, path: '/care/residents/new', roles: ['admin', 'staff'] },
  { label: 'Admission Forms', icon: FileSignature, path: '/care/forms', roles: ['admin', 'staff'] },
  { label: 'Billing & Invoices', icon: FileText, path: '/care/invoices', roles: ['admin', 'finance'] },
  { label: 'Payments', icon: CreditCard, path: '/care/payments', roles: ['admin', 'finance'] },
  { label: 'Receipts', icon: Receipt, path: '/care/receipts', roles: ['admin', 'finance'] },
  { label: 'Reports', icon: BarChart3, path: '/care/reports', roles: ['admin', 'staff', 'finance'] },
  { label: 'Users', icon: UserCog, path: '/care/users', roles: ['admin'] },
  { label: 'Settings', icon: Settings, path: '/care/settings', roles: ['admin'] },
];

export default function CareLayout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(getUser());

  useEffect(() => {
    if (!localStorage.getItem('care_token')) { nav('/care/login'); return; }
    care.get('/auth/me').then((r) => { setUser(r.data); localStorage.setItem('care_user', JSON.stringify(r.data)); }).catch(() => {});
  }, [nav]);

  const logout = () => { localStorage.removeItem('care_token'); localStorage.removeItem('care_user'); nav('/care/login'); };
  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const active = (p) => loc.pathname === p || (p !== '/care/residents' && loc.pathname.startsWith(p + '/')) ||
    (p === '/care/residents' && /^\/care\/residents\/(?!new)/.test(loc.pathname));

  const Side = () => (
    <div className="flex flex-col h-full bg-[#181B30]">
      <div className="px-5 pt-5 pb-4 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center"><HeartHandshake className="w-[18px] h-[18px] text-white" /></div>
        <div>
          <p className="font-bold text-white leading-tight">StayMii Care</p>
          <p className="text-xs text-teal-300 font-medium" data-testid="care-org-name">{user?.org_name || 'Care Management System'}</p>
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <Link key={n.path} to={n.path} onClick={() => setOpen(false)} data-testid={`care-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${active(n.path) ? 'bg-teal-600/20 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}>
            <n.icon className={`w-[17px] h-[17px] ${active(n.path) ? 'text-teal-400' : ''}`} />{n.label}
          </Link>
        ))}
      </nav>
      <button onClick={logout} data-testid="care-logout" className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-slate-400 hover:bg-white/5 hover:text-white">
        <LogOut className="w-[17px] h-[17px]" />Log out
      </button>
    </div>
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-slate-50" data-testid="care-layout">
      <aside className="hidden lg:block w-[230px] shrink-0"><Side /></aside>
      {open && (<>
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} />
        <div className="lg:hidden fixed inset-y-0 left-0 w-[230px] z-50"><Side /></div>
      </>)}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 shrink-0">
          <button className="lg:hidden text-slate-600" onClick={() => setOpen(!open)} data-testid="care-mobile-menu">{open ? <X /> : <Menu />}</button>
          <div className="hidden lg:block" />
          <div className="text-right" data-testid="care-user-badge">
            <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
            <p className="text-[11px] text-teal-700 capitalize">{user?.role}</p>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">{children}</main>
        <AskHost />
      </div>
    </div>
  );
}
